import { useState, useRef, useEffect } from 'react';
import PDFViewer from '../../../components/feature/PDFViewer';
import { WorkshopVideo } from '../../admin/components/WorkshopManager';

interface RecursosSectionProps {
  pptEmbedUrl: string;
  infographicUrl: string;
  manualUrl: string;
  workshopVideos: WorkshopVideo[];
  openedResources: string[];
  quizPassed: boolean;
  activeResourceId?: string | null;
  onOpenResource: (resourceId: string) => void;
}

interface Recurso {
  id: string;
  type: string;
  icon: string;
  color: string;
  title: string;
  subtitle: string;
  desc: string;
  tag: string;
  fileUrl?: string;
  isImage?: boolean;
  comingSoon?: boolean;
}

const tagColors: Record<string, string> = {
  Fundamentos: '#C97B63',
  Referencia: '#8B9D83',
  Visual: '#8B9D83',
  Práctica: '#C97B63',
};

interface PDFModalProps {
  recurso: Recurso;
  onClose: () => void;
}

function ResourceModal({ recurso, onClose }: PDFModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(13,15,18,0.92)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-5xl rounded-2xl overflow-hidden flex flex-col"
        style={{ background: '#16181C', border: '1px solid rgba(248,246,243,0.10)', maxHeight: '92vh', height: '92vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 flex-shrink-0"
          style={{ borderBottom: '1px solid rgba(248,246,243,0.07)' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 flex items-center justify-center rounded-xl"
              style={{ background: `${recurso.color}15`, border: `1px solid ${recurso.color}30` }}
            >
              <i className={`${recurso.icon} text-base`} style={{ color: recurso.color }} />
            </div>
            <div>
              <h3 className="text-[#F8F6F3] font-semibold text-sm">{recurso.title}</h3>
              <p className="text-[#F8F6F3]/40 text-xs">{recurso.subtitle}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 hover:opacity-70 cursor-pointer"
            style={{ background: 'rgba(248,246,243,0.06)' }}
          >
            <i className="ri-close-line text-[#F8F6F3]/70 text-base" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {recurso.isImage && recurso.fileUrl ? (
            <div className="w-full h-full overflow-auto flex justify-center bg-[#1A1A1F] p-6">
              <img
                src={recurso.fileUrl}
                alt={recurso.title}
                className="max-w-full h-auto rounded-xl object-contain"
                onContextMenu={(e) => e.preventDefault()}
                style={{ userSelect: 'none', pointerEvents: 'none' }}
                draggable={false}
              />
            </div>
          ) : recurso.fileUrl ? (
            <PDFViewer url={recurso.fileUrl} title={recurso.title} />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-5 px-8 text-center">
              <div
                className="w-20 h-20 flex items-center justify-center rounded-full"
                style={{ background: `${recurso.color}10`, border: `1px solid ${recurso.color}20` }}
              >
                <i className={`${recurso.icon} text-3xl`} style={{ color: recurso.color }} />
              </div>
              <div>
                <p className="text-[#F8F6F3]/70 font-semibold text-base mb-2">{recurso.title}</p>
                <p className="text-[#F8F6F3]/40 text-sm max-w-sm leading-relaxed">{recurso.desc}</p>
              </div>
              <div
                className="flex items-center gap-2 px-5 py-2.5 rounded-full text-xs"
                style={{ background: 'rgba(248,246,243,0.05)', border: '1px solid rgba(248,246,243,0.10)' }}
              >
                <i className="ri-time-line text-[#F8F6F3]/40 text-sm" />
                <span className="text-[#F8F6F3]/40">Material en preparación — disponible próximamente</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface WorkshopModalProps {
  video: WorkshopVideo;
  onClose: () => void;
}

function WorkshopModal({ video, onClose }: WorkshopModalProps) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(13,15,18,0.92)' }}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl rounded-2xl overflow-hidden flex flex-col"
        style={{ background: '#16181C', border: '1px solid rgba(248,246,243,0.10)', maxHeight: '88vh' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" style={{ borderBottom: '1px solid rgba(248,246,243,0.07)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl" style={{ background: 'rgba(139,157,131,0.12)', border: '1px solid rgba(139,157,131,0.25)' }}>
              <i className="ri-play-circle-line text-[#8B9D83] text-base" />
            </div>
            <div>
              <h3 className="text-[#F8F6F3] font-semibold text-sm">{video.title}</h3>
              <p className="text-[#F8F6F3]/40 text-xs">Taller Práctico</p>
            </div>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg transition-all duration-200 hover:opacity-70 cursor-pointer" style={{ background: 'rgba(248,246,243,0.06)' }}>
            <i className="ri-close-line text-[#F8F6F3]/70 text-base" />
          </button>
        </div>
        <div className="flex-1 overflow-auto min-h-0">
          {video.vimeo_id ? (
            <div style={{ aspectRatio: '16/9', background: '#0D0F12' }}>
              <iframe
                src={`https://player.vimeo.com/video/${video.vimeo_id}?badge=0&autopause=0&color=8B9D83&title=0&byline=0&portrait=0`}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={video.title}
                style={{ border: 'none' }}
              />
            </div>
          ) : (
            <div className="flex items-center justify-center py-16">
              <p className="text-[#F8F6F3]/30 text-sm">Video no disponible</p>
            </div>
          )}
          {video.description && (
            <div className="px-6 py-4">
              <p className="text-[#F8F6F3]/50 text-sm leading-relaxed">{video.description}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RecursosSection({
  pptEmbedUrl,
  infographicUrl,
  manualUrl,
  workshopVideos,
  openedResources,
  quizPassed,
  activeResourceId,
  onOpenResource,
}: RecursosSectionProps) {
  const [activeRecurso, setActiveRecurso] = useState<Recurso | null>(null);
  const [activeWorkshopVideo, setActiveWorkshopVideo] = useState<WorkshopVideo | null>(null);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const pptAvailable = !!pptEmbedUrl;
  const infografiaAvailable = !!infographicUrl;
  const manualAvailable = !!manualUrl;

  const isImageUrl = (url: string) =>
    /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(url);

  const recursos: Recurso[] = [
    {
      id: 'manual',
      type: 'PDF',
      icon: 'ri-file-pdf-line',
      color: '#C97B63',
      title: 'Manual del Nodo 0',
      subtitle: 'El Origen — Guía completa',
      desc: 'Documento con los conceptos fundamentales del sistema, ejercicios de anclaje y el marco teórico de la Re-ingeniería Espiritual.',
      tag: 'Fundamentos',
      fileUrl: manualAvailable ? manualUrl : undefined,
      comingSoon: !manualAvailable,
    },
    {
      id: 'ppt',
      type: 'Presentación',
      icon: 'ri-slideshow-line',
      color: '#8B9D83',
      title: 'Presentación del Módulo',
      subtitle: 'Slides del Nodo 0',
      desc: 'Presentación con los conceptos clave del Nodo 0 para seguir durante la clase o repasar después.',
      tag: 'Referencia',
      fileUrl: pptAvailable ? pptEmbedUrl : undefined,
      comingSoon: !pptAvailable,
    },
    {
      id: 'infografia',
      type: 'Infografía',
      icon: 'ri-image-2-line',
      color: '#8B9D83',
      title: 'Infografía del Nodo 0',
      subtitle: 'Arquitectura Visual',
      desc: 'Material visual que resume los conceptos esenciales del Nodo 0 de forma gráfica y estructurada.',
      tag: 'Visual',
      fileUrl: infografiaAvailable ? infographicUrl : undefined,
      isImage: infografiaAvailable ? isImageUrl(infographicUrl) : false,
      comingSoon: !infografiaAvailable,
    },
    {
      id: 'glosario',
      type: 'Glosario',
      icon: 'ri-draft-line',
      color: '#C97B63',
      title: 'Glosario Nodal',
      subtitle: 'Vocabulario del Sistema',
      desc: 'Diccionario completo de los términos del sistema Nube Geodésica. Tu referencia permanente durante todo el trazado.',
      tag: 'Práctica',
      comingSoon: true,
    },
  ];

  // Scroll to and open highlighted resource when coming from video tab
  useEffect(() => {
    if (!activeResourceId) return;
    const el = cardRefs.current[activeResourceId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    // Auto-open the resource modal if available
    const found = recursos.find((r) => r.id === activeResourceId);
    if (found && !found.comingSoon) {
      setTimeout(() => {
        onOpenResource(found.id);
        setActiveRecurso(found);
      }, 400);
    }
    // Check workshop videos
    if (activeResourceId.startsWith('taller_')) {
      const videoId = activeResourceId.replace('taller_', '');
      const video = workshopVideos.find((v) => v.id === videoId);
      if (video) {
        setTimeout(() => {
          onOpenResource(activeResourceId);
          setActiveWorkshopVideo(video);
        }, 400);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeResourceId]);

  const getTitleColor = (r: Recurso): string => {
    if (r.comingSoon) return 'rgba(248,246,243,0.55)';
    if (quizPassed || openedResources.includes(r.id)) return '#8B9D83';
    return '#C97B63';
  };

  const handleVerOnline = (r: Recurso) => {
    if (!r.comingSoon) onOpenResource(r.id);
    setActiveRecurso(r);
  };

  const handleOpenWorkshopVideo = (video: WorkshopVideo) => {
    onOpenResource(`taller_${video.id}`);
    setActiveWorkshopVideo(video);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="text-[#8B9D83] text-[10px] uppercase tracking-[3px] mb-1">Recursos del Nodo 0</p>
          <p className="text-[#F8F6F3]/40 text-sm">Materiales exclusivos para tu proceso</p>
        </div>
        <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(139,157,131,0.10)', border: '1px solid rgba(139,157,131,0.20)' }}>
          <i className="ri-shield-check-line text-[#8B9D83] text-sm" />
          <span className="text-[#8B9D83] text-xs">Visor integrado</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {recursos.map((r) => {
          const titleColor = getTitleColor(r);
          const isViewed = !r.comingSoon && (quizPassed || openedResources.includes(r.id));
          const isHighlighted = activeResourceId === r.id;

          return (
            <div
              key={r.id}
              ref={(el) => { cardRefs.current[r.id] = el; }}
              className="rounded-2xl p-6 flex flex-col gap-4 transition-all duration-300"
              style={{
                background: isHighlighted ? 'rgba(139,157,131,0.08)' : 'rgba(248,246,243,0.04)',
                border: isHighlighted
                  ? '1px solid rgba(139,157,131,0.35)'
                  : '1px solid rgba(248,246,243,0.08)',
              }}
            >
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${r.color}15`, border: `1px solid ${r.color}30` }}>
                  <i className={`${r.icon} text-xl`} style={{ color: r.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] uppercase tracking-[2px] px-2 py-0.5 rounded-full" style={{ background: `${tagColors[r.tag]}15`, color: tagColors[r.tag] }}>
                      {r.tag}
                    </span>
                    <span className="text-[#F8F6F3]/25 text-[10px]">{r.type}</span>
                  </div>
                  <h3 className="font-semibold text-sm transition-colors duration-300" style={{ color: titleColor }}>
                    {r.title}
                  </h3>
                  <p className="text-[#F8F6F3]/40 text-xs mt-0.5">{r.subtitle}</p>
                </div>
                {!r.comingSoon && (
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0 mt-1 transition-colors duration-300"
                    style={{ background: isViewed ? '#8B9D83' : '#C97B63', boxShadow: `0 0 6px ${isViewed ? 'rgba(139,157,131,0.6)' : 'rgba(201,123,99,0.6)'}` }}
                  />
                )}
              </div>

              <p className="text-[#F8F6F3]/50 text-sm leading-relaxed flex-1">{r.desc}</p>

              <div className="flex items-center justify-between pt-2" style={{ borderTop: '1px solid rgba(248,246,243,0.06)' }}>
                {r.comingSoon ? (
                  <span className="flex items-center gap-1.5 text-[#F8F6F3]/25 text-[10px]">
                    <i className="ri-time-line text-xs" /> Próximamente
                  </span>
                ) : isViewed ? (
                  <span className="flex items-center gap-1.5 text-[10px]" style={{ color: '#8B9D83' }}>
                    <i className="ri-checkbox-circle-line text-xs" /> Visto
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-[10px]" style={{ color: '#C97B63' }}>
                    <i className="ri-circle-line text-xs" /> Sin abrir
                  </span>
                )}
                <button
                  onClick={() => handleVerOnline(r)}
                  disabled={r.comingSoon}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200 hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-30 disabled:cursor-default"
                  style={{ background: 'rgba(139,157,131,0.15)', color: '#8B9D83', border: '1px solid rgba(139,157,131,0.30)' }}
                >
                  <i className="ri-eye-line text-xs" />
                  Ver online
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Taller práctico */}
      {workshopVideos.length > 0 && (
        <div className="mt-10">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="text-[#8B9D83] text-[10px] uppercase tracking-[3px] mb-1">Taller Práctico</p>
              <p className="text-[#F8F6F3]/40 text-sm">Ejercicios y actividades del módulo</p>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: 'rgba(139,157,131,0.10)', border: '1px solid rgba(139,157,131,0.20)' }}>
              <i className="ri-play-circle-line text-[#8B9D83] text-sm" />
              <span className="text-[#8B9D83] text-xs">{workshopVideos.length} ejercicio{workshopVideos.length !== 1 ? 's' : ''}</span>
            </div>
          </div>
          <div className="flex flex-col gap-4">
            {workshopVideos.map((video, index) => {
              const isViewed = quizPassed || openedResources.includes(`taller_${video.id}`);
              const isHighlighted = activeResourceId === `taller_${video.id}`;
              return (
                <div
                  key={video.id}
                  ref={(el) => { cardRefs.current[`taller_${video.id}`] = el; }}
                  className="rounded-2xl p-5 flex items-center gap-5 transition-all duration-300"
                  style={{
                    background: isHighlighted ? 'rgba(139,157,131,0.08)' : 'rgba(248,246,243,0.04)',
                    border: isHighlighted ? '1px solid rgba(139,157,131,0.35)' : '1px solid rgba(248,246,243,0.08)',
                  }}
                >
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: 'rgba(139,157,131,0.12)', border: '1px solid rgba(139,157,131,0.22)' }}>
                    <i className="ri-play-circle-line text-[#8B9D83] text-2xl" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] uppercase tracking-[2px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(139,157,131,0.12)', color: '#8B9D83' }}>
                        Ejercicio {index + 1}
                      </span>
                    </div>
                    <h4 className="font-semibold text-sm transition-colors duration-300" style={{ color: isViewed ? '#8B9D83' : '#C97B63' }}>
                      {video.title || `Ejercicio ${index + 1}`}
                    </h4>
                    {video.description && (
                      <p className="text-[#F8F6F3]/40 text-xs mt-1 leading-relaxed line-clamp-1">{video.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="w-2 h-2 rounded-full" style={{ background: isViewed ? '#8B9D83' : '#C97B63', boxShadow: `0 0 6px ${isViewed ? 'rgba(139,157,131,0.6)' : 'rgba(201,123,99,0.6)'}` }} />
                    <button
                      onClick={() => handleOpenWorkshopVideo(video)}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all duration-200 hover:opacity-90 cursor-pointer whitespace-nowrap"
                      style={{ background: 'rgba(139,157,131,0.15)', color: '#8B9D83', border: '1px solid rgba(139,157,131,0.30)' }}
                    >
                      <i className="ri-play-line text-xs" /> Ver ejercicio
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Info note */}
      <div className="mt-6 flex items-start gap-3 p-4 rounded-xl" style={{ background: 'rgba(248,246,243,0.03)', border: '1px solid rgba(248,246,243,0.05)' }}>
        <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
          <i className="ri-shield-check-line text-[#8B9D83]/50 text-sm" />
        </div>
        <p className="text-[#F8F6F3]/30 text-xs leading-relaxed">
          Todos los materiales se visualizan con el visor integrado de la plataforma. El contenido está protegido y solo puede verse aquí dentro.
        </p>
      </div>

      {activeRecurso && (
        <ResourceModal recurso={activeRecurso} onClose={() => setActiveRecurso(null)} />
      )}
      {activeWorkshopVideo && (
        <WorkshopModal video={activeWorkshopVideo} onClose={() => setActiveWorkshopVideo(null)} />
      )}
    </div>
  );
}
